import { Link as RouterLink } from 'react-router';

import { RefinancingHero } from '@/components/RefinancingHero/index.ts';
import { Button } from '@/ui/button-2.tsx';

import Page from '../../components/Page/index.ts';
import RrsHandler from '../../components/RrsHandler/index.ts';
import Section from '../../components/Section/index.ts';
import useBrainSession from '../../services/BrainSession/useBrainSession.ts';
import { LANG } from '../../services/Config.js';
import Disclaimer from '../Common/Disclaimer.tsx';

import { RRS_REFINANCING_LOANS } from './RRS.ts';

const linkClassName =
  'typography-v1-legacy-p3 font-normal text-[#18A0FB]! no-underline hover:underline';

/** Inline links inside `typography-v1-legacy-subtitle2` blocks — match body size, not `p3`. */
const linkClassNameSubtitle2 =
  'typography-v1-legacy-subtitle2 font-normal text-[#18A0FB]! no-underline hover:underline';

const rowClassName = 'mx-auto flex w-full max-w-[900px]! flex-col flex-wrap sm:flex-row';

/** Scroll + **`max-w-[900px]!`** beats `Section` `*:max-w-[1200px]` (same as `RepaymentPeriod`). */
const refinancingTableWrapClassName =
  'relative mx-auto mb-12 w-full max-w-[900px]! min-w-0 self-stretch overflow-x-auto rounded-lg border border-[#CEE2FF] shadow-[0px_2px_4px_rgba(0,0,0,0.02)] touch-pan-x';

const headCellCorner =
  'typography-v1-legacy-table-cell relative sticky left-0 z-10 w-[130px] min-w-[130px] border border-[#CEE2FF] bg-white px-4! py-2! align-top text-left font-normal text-v1-background-200 sm:min-w-[250px]';

const headCellNumeric =
  'typography-v1-legacy-table-cell border border-[#CEE2FF] px-4! py-2! align-top text-right font-normal text-[#18A0FB]';

const bodyRowHeadCellClassName =
  'typography-v1-legacy-table-cell sticky left-0 z-10 w-[130px] min-w-[130px] border border-[#CEE2FF] bg-white px-4! py-2! align-top text-left font-normal text-v1-background-200 sm:min-w-[250px]';

const bodyValueCellClassName =
  'typography-v1-legacy-table-cell border border-[#CEE2FF] px-4! py-2! align-top text-right font-normal text-[#18A0FB]';

const tableTopRow = ['Avdrag og sparing / År ', '1 år', '2 år', '3 år', '4 år', '5 år'];

const tableRows = [
  {
    title: 'Månedlige avdrag – 25 % rente',
    values: ['9504 kr', '5337 kr', '3976 kr', '3316 kr', '2935 kr'],
  },
  {
    title: 'Månedlige avdrag – 10 % rente',
    values: ['8792 kr', '4614 kr', '3227 kr', '2536 kr', '2125 kr'],
  },
  {
    title: 'Du sparer hver måned',
    values: ['712 kr', '723 kr', '749 kr', '780 kr', '810 kr'],
  },
  {
    title: 'Du sparer til sammen',
    values: ['8544 kr', '17.352 kr', '26.964 kr', '37.440 kr', '48.600 kr'],
  },
];

const RefinancingLoansTable = () => (
  <div className={refinancingTableWrapClassName}>
    <table className="w-full min-w-[690px] border-collapse border-hidden">
      <thead>
        <tr className="align-top">
          {tableTopRow.map((column, index) => {
            const isFirstColumn = index === 0;
            return (
              <th
                key={column}
                scope="col"
                className={isFirstColumn ? headCellCorner : headCellNumeric}
              >
                {isFirstColumn ? (
                  <span
                    className="absolute top-[-8px] left-[130px] h-[243px] w-px bg-[#CEE2FF] min-[680px]:hidden sm:left-[250px] sm:h-[170px]"
                    aria-hidden
                  />
                ) : null}
                {column}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {tableRows.map(({ title, values }, index) => {
          const isLastRow = index === 3;
          return (
            <tr key={title} className="align-top">
              <th scope="row" className={bodyRowHeadCellClassName}>
                {title}
              </th>
              {values.map((value) => (
                <td key={value} className={bodyValueCellClassName}>
                  {isLastRow ? <strong>{value}</strong> : value}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const debtCheckButtonClassName = 'max-w-[550px] self-center sm:min-w-[330px]';

const RefinancingLoans: React.FC = () => {
  const { appStrings, routes } = LANG;
  const { toggleChat } = useBrainSession();

  const handleDebtCheckClick = () => {
    toggleChat({
      goal: 'debt_check',
      step: 'refin_present_terms_auto_new',
    });
  };

  return (
    <Page title={appStrings.MENU.REFINANCING_LOANS}>
      <RrsHandler {...RRS_REFINANCING_LOANS} />
      <RefinancingHero chatStep="refin_present_terms_auto_new" />
      <Section>
        <div className="flex max-w-[900px]! flex-col">
          <h1 className="mb-6 typography-v1-legacy-h2 text-[#18A0FB] sm:mb-8">
            Refinansieringslån – så mye kan du spare
          </h1>
          <h2 className="mb-4 typography-v1-legacy-subtitle1Bold">
            Med et refinansieringslån, eller bare refinansiering som det også kalles, samler du dyre
            smålån og kredittkort til ett billigere lån. Fordelene når du kun har én bank å forholde
            deg til er mer oversikt, bedre kontroll, færre gebyrer, mindre kostnader, lengre
            nedbetalingstid – og lavere rente.
          </h2>
          <p className="typography-v1-legacy-subtitle2">
            Når du har flere smålån og kredittkort, kan det være vanskelig å vite til enhver tid om
            du er oppdatert på innbetalingene dine. Med mange små summer og stadig nye datoer å
            forholde seg til, kan livet ditt fort bli fylt av stress, vonde tanker og ta fra deg
            nattesøvnen.
            <br />
            <br />
            Det kanskje aller verste er alle de ekstra pengene du må betale fordi rentene er høye.
            Som et eksempel er det ikke uvanlig med 25 prosent rente på smålån og kredittkort. Det
            betyr at hver tusenlapp du skylder, koster deg 250 kroner. Skylder du 200.000 kroner til
            sammen betyr det 50.000 hvert år, noe som er mer enn 4000 i måneden – det er mye penger.
            <br />
            <br />
            Heldigvis kan det finnes en løsning for deg, og den heter refinansieringslån. Det er
            gratis, enkelt og uforpliktende,{' '}
            <RouterLink className={linkClassNameSubtitle2} to={routes.PRODUCTS_REFINANCING}>
              og det tar deg kun to minutter å søke.
            </RouterLink>
            <br />
            <br />
            <RouterLink className={linkClassNameSubtitle2} to={routes.INTEREST_RATE}>
              Nominell eller effektiv – hva er egentlig rente?
            </RouterLink>
          </p>
        </div>
      </Section>
      <Section background="paper" last>
        <div className={rowClassName}>
          <div className="w-full sm:w-1/2 sm:pr-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Hva er et refinansieringslån?
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Et refinansieringslån er et lån uten sikkerhet, altså det som vanligvis heter
              forbrukslån. Likevel er det litt annerledes, for når du får innvilget et
              refinansieringslån hjelper banken din deg som regel å betale kreditorene dine. Med
              andre ord slipper du å gjøre jobben selv, du blir kvitt all gammel gjeld, og du kan
              starte på nytt igjen – med én bank å forholde deg til. Det gir deg trygghet.
              <br />
              <br />
              Visste du forresten at du må nedbetale forbrukslån innen 5 år, mens du på
              refinansieringslån kan bruke inntil 15 år? Det kan gi deg en hverdag med større
              økonomisk frihet.
              <br />
              <br />
              <strong>
                Når det er sagt –{' '}
                <RouterLink className={linkClassName} to={routes.REPAYMENT_PERIOD}>
                  det lønner seg alltid å nedbetale gjeld så raskt som mulig.
                </RouterLink>
              </strong>
            </p>
          </div>

          <div className="w-full sm:w-1/2 sm:pl-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Hvor mye kan jeg spare med et refinansieringslån?
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Vi sier det som det er – ikke alle får lavere rente når de søker om
              refinansieringslån. Hvilke betingelser du får avhenger av økonomien din. Under
              kredittvurderingen tas det blant annet hensyn til inntekter, utgifter, betjeningsevne
              og om du har annen gjeld. Med det sagt opplever de aller fleste at de får vesentlig
              bedre betingelser med et refinansieringslån.
              <br />
              <br />
              Og du kan spare store penger. Sett at du har smålån og kredittkortlån på til sammen
              100.000 kroner med en gjennomsnittsrente på 25 prosent. Tabellen viser deg hvor mye du
              sparer dersom du får innvilget et refinansieringslån og betaler ned over 1, 2, 3, 4
              eller 5 år.
            </p>
          </div>

          <RefinancingLoansTable />

          <div className="w-full sm:w-1/2 sm:pr-6">
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Som du ser kan du i løpet av 5 år spare nesten 50.000 kroner med et
              refinansieringslån. Det betyr at om du får et refinansieringslån på 500.000 kroner,
              kan du i prinsippet spare nærmere en kvart million kroner.
              <br />
              <br />
              <strong>
                Hvor mye gjeld har du?{' '}
                <RouterLink className={linkClassName} to={routes.DEBT_OVERVIEW}>
                  Få full oversikt i Gjeldsregisteret.
                </RouterLink>
              </strong>
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Hvordan søker jeg om refinansieringslån?
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Å få det beste refinansieringslånet for deg kan altså bety mange tusen kroner i
              måneden. Det er ofte vanskelig å finne banken som er din match når du søker selv, og
              derfor er det smart å bruke sammenligningstjenesten Scoopr – det sparer du mye tid og
              arbeid på.
            </p>
          </div>

          <div className="w-full sm:w-1/2 sm:pl-6">
            <p className="typography-v1-legacy-p3">Så enkelt gjør du det:</p>
            <ul className="typography-v1-legacy-list typography-v1-legacy-p3">
              <li>
                Finn frem BankID-en din, og{' '}
                <RouterLink className={linkClassName} to={routes.PRODUCTS_REFINANCING}>
                  søk her om refinansieringslån fra 20.000 til 500.000 kroner.
                </RouterLink>
              </li>
              <li>
                Så snart du er ferdig, overtar Scoopr. Da går søknaden din automatisk ut til 13
                banker, som alle konkurrerer om å gi deg så lav rente de klarer.
              </li>
              <li>
                Når du har mottatt tilbudene fra bankene, har du 30 dager på å bestemme deg for om
                du ønsker et refinansieringslån – eller ikke.
              </li>
              <li>
                Etter du har valgt bank, gjør de resten. Det eneste du må gjøre, er å sørge for å
                overholde{' '}
                <RouterLink className={linkClassName} to={routes.REPAYMENT_PERIOD}>
                  nedbetalingsplanen.
                </RouterLink>
              </li>
            </ul>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              <strong>
                Vil du få oversikten og kontrollen tilbake? La Scoopr sammenligne refinansieringslån
                for deg –{' '}
                <RouterLink className={linkClassName} to={routes.REPAYMENT_PERIOD}>
                  det tar deg kun to minutter å søke.
                </RouterLink>
              </strong>
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center">
          <Button
            color="yellow"
            className={debtCheckButtonClassName}
            onClick={handleDebtCheckClick}
          >
            Sjekk nå
          </Button>
          <Disclaimer />
        </div>
      </Section>
    </Page>
  );
};

export default RefinancingLoans;
